import React, {
  useEffect, useCallback, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { commerceProductChangeQuantity } from '@uncinc/react-drupal-core/dist/redux/actions/Commerce';
import useCartProduct from '../../helpers/useCartProduct';
import { type ProductType } from '../../helpers/digitalProductHelpers';
import CartProductImage from '../../CartProduct/CartProductImage';
import DiscountTierAccordion from '../../../../../components/DiscountTierAccordion';

import './style.scss';

/**
 * Debounce delay for quantity API calls (ms).
 * Batches rapid clicks into a single request to prevent
 * OrderVersionMismatchException on the backend.
 */
const QUANTITY_UPDATE_DEBOUNCE_MS = 400;

/**
 * Product Overview Section Component
 *
 * Displays product title and quantity selector for digital product checkout.
 * For paid products, shows a quantity counter with increment/decrement buttons.
 * For free products, quantity selector is hidden.
 *
 * @component
 */
interface ProductOverviewSectionProps {
  /** Title of the product being purchased */
  productTitle: string | null;
  /** Type of product in cart - determines which UI elements to show */
  productType: ProductType;
}

const ProductOverviewSection: React.FC<ProductOverviewSectionProps> = ({
  productTitle,
  productType,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    cartProductId,
    cartProductQuantity,
    localQuantity,
    setLocalQuantity,
    cartProductPrice,
    cartProductImage,
    maximumQuantity,
    discountTiers,
    body,
  } = useCartProduct();

  const isPaidProduct = Number(cartProductPrice) !== 0;

  const currentQty =
    typeof localQuantity === 'number'
      ? localQuantity
      : cartProductQuantity || 1;

  // Track whether a quantity update API call is in progress
  const [isUpdating, setIsUpdating] = useState(false);

  // Track whether the user tried to exceed the max quantity
  const [showMaxMessage, setShowMaxMessage] = useState(false);

  // Ref for debounce timer — cleared on each new input so only the
  // final value in a burst of clicks is sent to the backend
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up debounce timer on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  // Sync local state with cart product quantity
  useEffect(() => {
    // Ensure it's always an integer (no decimals from API)
    const quantity = Math.floor(cartProductQuantity || 1);
    setLocalQuantity(quantity);

    // Cart has been reloaded with the new quantity — unlock the UI
    setIsUpdating(false);
  }, [cartProductQuantity, setLocalQuantity]);

  /**
   * Updates the cart quantity via Redux.
   * Debounced to prevent concurrent API calls that cause
   * OrderVersionMismatchException on the Drupal backend.
   *
   * The local UI state updates immediately for responsiveness;
   * only the API call is debounced.
   *
   * Note: commerceProductChangeQuantity already calls commerceLoadCart()
   * internally, so we don't dispatch it separately.
   *
   * @param newQuantity - The new quantity to set
   */
  const updateCartQuantity = useCallback(
    (newQuantity: number): void => {
      if (
        !cartProductId ||
        newQuantity === cartProductQuantity ||
        newQuantity < 1
      ) {
        return;
      }

      // Cancel any pending debounced call — only the latest value matters
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setIsUpdating(true);
        // @ts-expect-error - Redux thunk dispatch returns function, not AnyAction
        dispatch(commerceProductChangeQuantity(cartProductId, newQuantity));
      }, QUANTITY_UPDATE_DEBOUNCE_MS);
    },
    [cartProductId, cartProductQuantity, dispatch],
  );

  /**
   * Handles decrement button click
   * Decreases quantity by 1, minimum value is 1
   */
  const handleDecrement = useCallback((): void => {
    const currentValue = typeof localQuantity === 'number' ? localQuantity : 1;
    const newValue = Math.max(1, currentValue - 1);
    setShowMaxMessage(false);
    setLocalQuantity(newValue);
    updateCartQuantity(newValue);
  }, [localQuantity, setLocalQuantity, updateCartQuantity]);

  /**
   * Handles increment button click
   * Increases quantity by 1, capped at maximumQuantity if set
   */
  const handleIncrement = useCallback((): void => {
    const currentValue = typeof localQuantity === 'number' ? localQuantity : 1;
    const newValue = currentValue + 1;
    if (maximumQuantity && newValue > maximumQuantity) {
      setShowMaxMessage(true);
      return;
    }
    setShowMaxMessage(false);
    setLocalQuantity(newValue);
    updateCartQuantity(newValue);
  }, [localQuantity, maximumQuantity, setLocalQuantity, updateCartQuantity]);

  /**
   * Handles direct input change for quantity
   * Allows empty string for user to clear field and type new number
   */
  const handleQuantityChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { value } = event.target;

    // Allow empty string for user to clear field and type new number
    if (value === '') {
      setLocalQuantity('');
      return;
    }

    // Remove any commas that browser might add
    const cleanValue = value.replace(/,/g, '');
    let newValue = parseInt(cleanValue, 10);

    // If invalid, don't update (user might still be typing)
    if (Number.isNaN(newValue)) {
      return;
    }

    // Ensure it's always an integer (no decimals)
    newValue = Math.floor(newValue);

    // Update local state immediately for responsive UI
    setLocalQuantity(newValue);
  };

  /**
   * Handles blur event on quantity input
   * Validates final value and syncs with cart
   */
  const handleQuantityBlur = (
    event: React.FocusEvent<HTMLInputElement>,
  ): void => {
    // Clean up any commas when input loses focus
    const cleanValue = event.target.value.replace(/,/g, '');
    let newValue = parseInt(cleanValue, 10);

    newValue = Math.floor(newValue);

    // If valid, use the new value (clamped to max if set)
    if (!Number.isNaN(newValue) && newValue >= 1) {
      const clampedValue = maximumQuantity
        ? Math.min(newValue, maximumQuantity)
        : newValue;
      setShowMaxMessage(
        maximumQuantity !== undefined && newValue > maximumQuantity,
      );
      setLocalQuantity(clampedValue);
      updateCartQuantity(clampedValue);
    } else {
      // Reset to last valid quantity if input was invalid
      setLocalQuantity(Math.floor(cartProductQuantity || 1));
    }
  };

  return (
    <fieldset className="ProductOverviewSection">
      {productType === 'e_learning' && (
        <legend className="ProductOverviewSection__legend">
          {t('checkout.e_learning')}
        </legend>
      )}
      <div className="ProductOverviewSection__card">
        <div
          className={`ProductOverviewSection__image${
            !isPaidProduct ? ' ProductOverviewSection__image--free' : ''
          }`}
        >
          <CartProductImage image={cartProductImage} />
        </div>
        <div className="ProductOverviewSection__content">
          <h4 className="ProductOverviewSection__title">{productTitle}</h4>
          {body && (
            <p className="ProductOverviewSection__description">{body}</p>
          )}
          {discountTiers.length > 0 && (
            <DiscountTierAccordion
              tiers={discountTiers}
              activeQty={currentQty}
            />
          )}
          {isPaidProduct && (
            <div className="ProductOverviewSection__quantity">
              <label
                htmlFor="seats"
                className="ProductOverviewSection__quantity-label"
              >
                {t('checkout.number_of_seats')}
              </label>
              <div className="ProductOverviewSection__quantity-counter">
                <button
                  type="button"
                  className="ProductOverviewSection__quantity-button"
                  onClick={handleDecrement}
                  disabled={
                    !cartProductId ||
                    isUpdating ||
                    (typeof localQuantity === 'number' && localQuantity <= 1)
                  }
                  aria-label={t('checkout.decrease_quantity')}
                >
                  −
                </button>
                <input
                  id="seats"
                  type="number"
                  className="ProductOverviewSection__quantity-input"
                  value={localQuantity}
                  onChange={handleQuantityChange}
                  onBlur={handleQuantityBlur}
                  min="1"
                  step="1"
                  disabled={!cartProductId || isUpdating}
                  aria-label={t('checkout.number_of_seats')}
                />
                <button
                  type="button"
                  className="ProductOverviewSection__quantity-button"
                  onClick={handleIncrement}
                  disabled={!cartProductId || isUpdating}
                  aria-label={t('checkout.increase_quantity')}
                >
                  +
                </button>
              </div>
              {showMaxMessage && maximumQuantity !== undefined && (
                <p className="ProductOverviewSection__quantity-limit">
                  {t('cart.maximum_quantity_per_order', {
                    maximumQuantity,
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </fieldset>
  );
};

export default ProductOverviewSection;
